import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import {
  Package, ReceiptText, KeyRound, UserRound, ChevronDown, ChevronRight, LogOut, Menu,
} from 'lucide-react'
import { Dropdown } from '@heroui/react'
import { useAuthStore } from '@/stores/auth'
import './MainLayout.scss'

export default function MainLayout() {
  const { t } = useTranslation()
  const auth = useAuthStore()
  const route = useLocation()
  const navigate = useNavigate()
  // 手机端导航抽屉：桌面端由 CSS 隐藏按钮、常显侧边栏
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => {
    setNavOpen(false)
  }, [route.pathname])

  const nav = useMemo(
    () => [
      { path: '/products', label: 'nav.products', icon: Package },
      { path: '/orders', label: 'nav.orders', icon: ReceiptText },
      { path: '/licenses', label: 'nav.licenses', icon: KeyRound },
      { path: '/profile', label: 'nav.profile', icon: UserRound },
    ],
    [],
  )

  const crumbs = useMemo(() => {
    const last = nav.find((n) => route.pathname.startsWith(n.path))
    const items = [t('brand.name'), t('crumb.portal')]
    if (last) items.push(t(last.label))
    if (route.pathname.startsWith('/orders/')) items[items.length - 1] = t('orders.detail')
    if (route.pathname.startsWith('/licenses/')) items[items.length - 1] = t('licenses.detail')
    return items
  }, [nav, route.pathname, t])

  function isActive(path: string) {
    return route.pathname.startsWith(path)
  }

  async function signOut() {
    await auth.logout()
    navigate('/login')
  }

  return (
    <div className="shell">
      <aside className={`aside${navOpen ? ' is-open' : ''}`}>
        <div className="logo-box">
          <span className="wordmark">{t('brand.name')}</span>
        </div>
        <nav className="nav">
          {nav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item${isActive(item.path) ? ' is-active' : ''}`}
              onClick={() => setNavOpen(false)}
            >
              <item.icon size={16} strokeWidth={2} />
              <span>{t(item.label)}</span>
            </Link>
          ))}
        </nav>
      </aside>
      {navOpen && <div className="backdrop" onClick={() => setNavOpen(false)} />}
      <div className="main">
        <header className="header">
          <div className="header-left">
            <button type="button" className="menu-btn" aria-label="menu" onClick={() => setNavOpen(true)}>
              <Menu size={18} />
            </button>
            <div className="breadcrumb">
            {crumbs.map((c, i) => (
              <span key={i} className="crumb-wrap">
                {i > 0 && (
                  <span className="sep">
                    <ChevronRight size={14} />
                  </span>
                )}
                <span className={`crumb${i === crumbs.length - 1 ? ' is-current' : ''}`}>{c}</span>
              </span>
            ))}
            </div>
          </div>
          <Dropdown>
            <Dropdown.Trigger className="account">
              {auth.profile?.email || '—'}
              <ChevronDown size={14} />
            </Dropdown.Trigger>
            <Dropdown.Popover placement="bottom end">
              <Dropdown.Menu onAction={(key) => key === 'signout' && signOut()}>
                <Dropdown.Item id="signout" className="menu-item-inner">
                  <LogOut size={14} /> {t('nav.signOut')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
